import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Avatar, Box, Button, Chip, Menu, MenuItem } from '@mui/material'
import { styled } from '@mui/material/styles'
import moment from 'moment'
import {
  forwardRef,
  ReactElement,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReactComponent as CheckmarkIcon } from '../../../assets/icons/checkmark-new.svg'
import { ReactComponent as HIcon } from '../../../assets/icons/H-new.svg'
import { ReactComponent as CloseIcon } from '../../../assets/icons/X-new.svg'
import { AppContext } from '../../../providers/context/App'
import { EstheticianListContext } from '../../../providers/context/EstheticianList'
import { generateAvatarText } from '../../../utils/helper-functions'
import { Esthetician } from '../../../utils/types/estheticianTypes'
import './filter.scss'

const StyledMenu: any = styled((props) => (
  <Menu
    open={true}
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 5,
    marginTop: theme.spacing(1),
    width: '100%',
    maxWidth: 465,
    maxHeight: 360,
    border: '0.5px solid var(--navy)',
    boxShadow:
      '6px 8px 12px rgba(0, 0, 0, 0.04), 1px 1px 4px rgba(0, 0, 0, 0.08), 4px 6px 8px rgba(0, 0, 0, 0.06)',
    '& .MuiMenu-list': {
      padding: '0',
    },
    '& .MuiMenuItem-root': {
      borderBottom: '1px solid var(--navy-light)',
      padding: '16px 24px',
    },
    '& .MuiMenuItem-root:hover': {
      backgroundColor: 'transparent',
    },
  },
}))

const FilterComponent = forwardRef((props: any, ref): ReactElement => {
  const { est } = props
  const { estheticianMasterData, updateData: estheticianUpdateData } =
    useContext(EstheticianListContext)
  const { appMasterData, updateData: filterDataUpdate } = useContext(AppContext)
  const [anchorEl, setAnchorEl] = useState(null)

  const initialEst = useMemo(
    () => [...(est ?? appMasterData?.filteredEstheticians ?? [])],
    [est, appMasterData?.filteredEstheticians]
  )

  const [selectedEsthetician, setSelectedEsthetician] =
    useState<Array<Esthetician>>(initialEst)

  const isEstInitiated = useRef(false)

  useEffect(() => {
    if (isEstInitiated.current || !initialEst.length) {
      return
    }
    isEstInitiated.current = true
    setSelectedEsthetician(initialEst)
  }, [initialEst])

  const open = Boolean(anchorEl)
  const filterBtnRef = useRef<HTMLButtonElement>(null)

  const handleFilterBtnClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (event: any) => {
    setAnchorEl(null)
  }
  const handleDelete = (esthetician: Esthetician) => {
    setSelectedEsthetician(
      selectedEsthetician.filter((esth) => esth.id !== esthetician.id)
    )
  }

  const handleEstheticianClick = (event: any, esthetician: Esthetician) => {
    if (!selectedEsthetician.some((esth) => esth.id === esthetician.id)) {
      setSelectedEsthetician([...selectedEsthetician, esthetician])
    } else {
      setSelectedEsthetician(
        selectedEsthetician.filter((esth) => esth.id !== esthetician.id)
      )
    }
  }

  useImperativeHandle(ref, () => ({
    handleFilterBtnClick() {
      if (filterBtnRef.current !== null) {
        window.scrollTo(0, 0)
        filterBtnRef.current.click() //Trigger click
      }
    },
  }))

  useEffect(() => {
    estheticianUpdateData({ filteredEstheticians: selectedEsthetician })
    filterDataUpdate({ filteredEstheticians: selectedEsthetician })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEsthetician])

  return (
    <div className="filter">
      <Button
        id="filter-button"
        aria-controls={'filter-menu'}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : undefined}
        variant="contained"
        disableElevation
        ref={filterBtnRef}
        onClick={handleFilterBtnClick}
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      >
        Filter
      </Button>
      <StyledMenu
        className="filter-menu"
        id="filter-menu"
        MenuListProps={{
          'aria-labelledby': 'filter-button',
          role: 'listbox',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {estheticianMasterData.estheticians.map((esthetician) => {
          if (!esthetician.isAny) {
            return (
              <MenuItem
                key={esthetician.id}
                onClick={(event) => handleEstheticianClick(event, esthetician)}
                className="esthetician-item"
                disableRipple
              >
                <Avatar className="name-avatar">
                  {generateAvatarText(
                    esthetician.staff.firstName,
                    esthetician.staff.lastName
                  )}
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="name-text">
                    {esthetician.staff.firstName}{' '}
                    {esthetician.staff.lastName.charAt(0)}.
                  </span>
                  {esthetician.lastSeen && (
                    <span className="lastseen-text">
                      Last seen{' '}
                      {moment(esthetician.lastSeen).format('MM/DD/YYYY')}
                    </span>
                  )}
                </Box>
                <div className="check-icon">
                  {selectedEsthetician.some(
                    (esth) => esth.id === esthetician.id
                  ) && <CheckmarkIcon />}
                </div>
              </MenuItem>
            )
          } else {
            return (
              <MenuItem
                key={esthetician.id}
                onClick={(event) => handleEstheticianClick(event, esthetician)}
                className="esthetician-item"
                disableRipple
              >
                <Chip
                  className="any-chip"
                  avatar={
                    <Avatar>
                      <HIcon />
                    </Avatar>
                  }
                  label="Any Esthetician"
                />
                <div className="check-icon">
                  {selectedEsthetician.some(
                    (esth) => esth.id === esthetician.id
                  ) && <CheckmarkIcon />}
                </div>
              </MenuItem>
            )
          }
        })}
      </StyledMenu>
      {/* selected esthetician chips */}
      <Box sx={{ display: 'inline-flex' }} className="selected-pills">
        {selectedEsthetician.map((esthetician) => {
          return (
            <Chip
              key={esthetician.id}
              label={`${
                esthetician.staff.firstName
              } ${esthetician.staff.lastName.charAt(0)}.`}
              onDelete={() => handleDelete(esthetician)}
              deleteIcon={<CloseIcon className="close-icon" />}
              variant="outlined"
            />
          )
        })}
      </Box>
    </div>
  )
})

export default FilterComponent
